import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '@/shell/AppShell'
import { Chip } from '@/components/forge/Chip'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeAIButton } from '@/shell/ForgeAIButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Num } from '@/components/forge/primitives'
import { useAnomalies } from '@/features/anomalies/queries'
import { usePermission } from '@/features/auth/permissions'
import { useUsers } from '@/features/users/queries'
import { MAX_PAGE_SIZE, WO_PRIORITIES, WO_STATES, nextStateOf } from '@/features/workOrders/api'
import {
  useAdvanceWorkOrder,
  useCreateWorkOrder,
  useDeleteWorkOrder,
  useSaveWorkOrder,
  useWorkOrder,
  useWorkOrderBoard,
  useWorkOrderListStream,
  useWorkOrderStream,
} from '@/features/workOrders/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { formatInteger } from '@/lib/format'
import { WorkOrderBoard } from './WorkOrderBoard'
import { WorkOrderDetailDialog } from './WorkOrderDetailDialog'
import { WorkOrderFormDialog } from './WorkOrderFormDialog'
import { WorkOrderTable } from './WorkOrderTable'

/**
 * Work Orders.
 *
 * LIVE. Board and table over one collection, with the detail and form dialogs.
 *
 * ── The pickers come from other resources ─────────────────────────────────
 * `list_work_orders` serves no assignee list and no place list, so the form
 * reads the directory itself and chooses the place through `PlaceField`, which
 * drills the hierarchy a generation at a time. Folding either into the
 * work-order payload would send the whole estate down the wire on every page of
 * a list.
 *
 * ── Two permissions, because the API has two answers ──────────────────────
 * Reading the board, opening an order and saving one accept
 * `work_orders.manage` OR `work_orders.update_assigned`. Raising a new order
 * and deleting one need `work_orders.manage` alone. So a technician sees the
 * board and can move their own jobs along, and is not offered New WO or Delete
 * — which would 403 on click.
 *
 * ── The view and the open order live in the URL ───────────────────────────
 * `?view=table`, `?wo=WO-7734`, `?new=1`. So a colleague can be sent to one work
 * order, and — the reason this module was built next — the Timeseries
 * Workstation and the asset screens can link straight into a prefilled New WO
 * form. The prototype keeps all of it in `useState`, so its board cannot be
 * linked to at all.
 *
 * ── The stat strip is counted, not authored ───────────────────────────────
 * Over the whole collection rather than the filtered set, so "3 overdue" does
 * not become "0 overdue" the moment somebody filters to Completed. The column
 * counts on the board are the opposite — those describe the board in front of
 * you. Different questions, and each is answered where it is asked.
 */

/** What the filters start from and what Clear returns to. */
const EMPTY = { status: 'all', priority: 'all', assigneeId: 'all', source: 'all' }

export function WorkOrdersPage() {
  const { t } = useTranslation(['app', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()

  /**
   * Both gates. Neither is the authorisation — the API checks every request.
   * They keep a control off the screen that the server would refuse.
   */
  const manage = usePermission('work_orders.manage')
  const updateAssigned = usePermission('work_orders.update_assigned')
  const gatePending = manage.isPending || updateAssigned.isPending
  const canRead = manage.granted || updateAssigned.granted
  const canManage = manage.granted

  const view = searchParams.get('view') === 'table' ? 'table' : 'board'
  const openId = searchParams.get('wo')

  const [filters, setFilters] = useState(EMPTY)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)

  useDocumentTitle(`${t('workOrders.title')} · ${t('common:brand.name')}`)

  const params = useMemo(
    () => ({
      ...filters,
      q: query.trim() || undefined,
      /* A board wants every card; the endpoint has no unpaged mode, so it takes
         the largest page there is and page 1 of it. The column headers come
         from `counts.status`, which is over the whole board rather than this
         page, so the totals stay honest past 200. */
      page: view === 'table' ? page : 1,
      pageSize: view === 'table' ? pageSize : MAX_PAGE_SIZE,
    }),
    [filters, query, view, page, pageSize],
  )

  const board = useWorkOrderBoard(params, { enabled: canRead })
  /* Live: any order raised, moved or edited in the reader's scope refetches the
     board. A delete made elsewhere is not announced — see `liveStream.js`. */
  useWorkOrderListStream({ enabled: canRead })

  const create = useCreateWorkOrder()
  const save = useSaveWorkOrder()
  const advance = useAdvanceWorkOrder()
  const remove = useDeleteWorkOrder()

  const orders = board.data?.items ?? []
  const stats = board.data?.stats ?? { open: 0, ai: 0 }

  /* An order named in the URL. Found in the loaded set rather than fetched
     separately — the board already holds it, and a second request for a record
     on screen is a spinner over data we have. If a filter hides it, the param is
     dropped rather than left pointing at nothing. */
  const open = openId ? (orders.find((order) => order.id === openId) ?? null) : null
  /* …and then read in full, and kept live while it is open. The list row has
     no `notes`, so the dialog shows — and Edit prefills from — the detail; the
     row stands in only until it arrives. */
  const openDetail = useWorkOrder(openId, { enabled: canRead && Boolean(open) })
  useWorkOrderStream(openId, { enabled: openDetail.isSuccess })
  useEffect(() => {
    if (openId && !board.isPending && !open) setParam('wo', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, open, board.isPending])

  /* A prefilled New WO, arrived at from another module. `?new=1&assetId=…` is
     what the Workstation's insight banner and the asset screens link to. */
  const prefill = useMemo(() => {
    if (!searchParams.get('new')) return null
    return {
      assetId: searchParams.get('assetId') ?? '',
      title: searchParams.get('title') ?? '',
      anomalyId: searchParams.get('alertId') ?? null,
      priority: WO_PRIORITIES.includes(searchParams.get('priority'))
        ? searchParams.get('priority')
        : 'medium',
    }
  }, [searchParams])

  /* The two pickers, from the resources that own them.
   *
   * Below `prefill` because they read it: a `const` is in its temporal dead zone
   * until its own line runs, so referencing it above here is a ReferenceError on
   * first render rather than an undefined — which is how this landed the first
   * time.
   *
   * Only fetched once a form is actually open. A directory read on every visit
   * to a board nobody is editing is a request for nothing. The place is chosen
   * through `PlaceField`, which drills the hierarchy itself, so there is no
   * whole-estate read here any more. */
  const formOpen = creating || Boolean(prefill) || Boolean(editing)
  const people = useUsers({ isActive: true, pageSize: 200 }, { enabled: formOpen && canRead })

  /**
   * The anomalies a new order can be raised against.
   *
   * ── Behind its own permission ─────────────────────────────────────────
   * `list_anomalies` requires `anomalies.manage`, which is not implied by
   * either work-order permission. Somebody may raise work and not read the
   * anomaly register, so the picker is offered only when the list is readable
   * and the form says so rather than showing an empty select.
   *
   * ── Open ones only, and not polled ────────────────────────────────────
   * `resolved` and `dismissed` are finished; raising work against one is almost
   * always a mistake, and the few legitimate cases can still be reached by
   * arriving from the anomaly's own screen, which prefills the id and keeps it
   * whether or not this list carries it. Nothing polls: `useAnomalies` is off by
   * default now, and a dialog does not need the register refreshing underneath
   * it.
   */
  const canPickAnomaly = usePermission('anomalies.manage').granted
  const anomalyList = useAnomalies(
    { status: 'open', pageSize: 200 },
    { enabled: formOpen && !editing && canPickAnomaly },
  )
  const anomalies = anomalyList.data?.items ?? []

  const assignees = useMemo(
    () => (people.data?.items ?? []).map((person) => ({ id: person.id, name: person.name })),
    [people.data],
  )

  function setParam(key, value) {
    setSearchParams((previous) => {
      const url = new URLSearchParams(previous)
      if (value) url.set(key, value)
      else url.delete(key)
      return url
    })
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
    if (searchParams.get('new')) {
      setSearchParams((previous) => {
        const url = new URLSearchParams(previous)
        for (const key of ['new', 'assetId', 'alertId', 'title', 'priority']) url.delete(key)
        return url
      })
    }
  }

  const commit = (next) => {
    setFilters(next)
    // Any change to what is being asked for returns to page one. Without this a
    // filter that leaves four rows strands the reader on page three of nothing.
    setPage(1)
  }

  /* A full replace, because `WorkOrderUpdate` is one — the order carries its own
     other five fields back. See `advanceWorkOrder`. */
  const move = async (order, status) => {
    await advance.mutateAsync({ order, status })
    toast(
      t('workOrders.moved', {
        id: order.serialNumber,
        state: t(`workOrders.state.${status}`),
      }),
      'ok',
    )
  }

  const activeFilters = Object.values(filters).filter((value) => value !== 'all').length

  return (
    <Page>
      <header className="mb-4 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h1 className="m-0 text-display text-balance">{t('workOrders.title')}</h1>
          </div>
          <p className="mt-2 mb-0 text-body text-ink-dim">
            <Num>
              {t('workOrders.subtitle', {
                open: formatInteger(stats.open),
                ai: formatInteger(stats.ai),
              })}
            </Num>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label={t('workOrders.viewLabel')}
            className="flex gap-[3px] rounded-md bg-sunken p-[3px]"
          >
            {['board', 'table'].map((entry) => (
              <button
                key={entry}
                type="button"
                aria-pressed={view === entry}
                onClick={() => setParam('view', entry === 'board' ? null : entry)}
                className={
                  view === entry
                    ? 'rounded-xs bg-forge px-3 py-1 text-body-sm font-semibold text-white'
                    : 'rounded-xs px-3 py-1 text-body-sm text-ink-dim transition-colors hover:text-ink'
                }
              >
                {t(`workOrders.view.${entry}`)}
              </button>
            ))}
          </div>
          {canManage ? (
            <>
              <ForgeAIButton action="createWorkOrder" />
              <ForgeButton variant="primary" onClick={() => setCreating(true)}>
                <Icon name="plus" size="xs" />
                {t('workOrders.new')}
              </ForgeButton>
            </>
          ) : null}
        </div>
      </header>

      {/* No KPI strip. The design carries these counts in the SUBTITLE — "8
          open · 4 AI-generated" — and putting them in four cards as well says
          the same thing twice, on the screen where the board's own column
          headers are already counting. Open and AI-drafted stay in the
          subtitle; urgent and overdue are what the priority filter and the
          overdue badge on a card are for. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ForgeSelect
          label={t('workOrders.column.status')}
          labelInline
          size="sm"
          value={filters.status}
          onValueChange={(value) => commit({ ...filters, status: value })}
          options={[
            { value: 'all', label: t('workOrders.filter.anyState') },
            ...WO_STATES.map((state) => ({ value: state, label: t(`workOrders.state.${state}`) })),
          ]}
        />
        <ForgeSelect
          label={t('workOrders.column.priority')}
          labelInline
          size="sm"
          value={filters.priority}
          onValueChange={(value) => commit({ ...filters, priority: value })}
          options={[
            { value: 'all', label: t('workOrders.filter.anyPriority') },
            ...WO_PRIORITIES.map((p) => ({ value: p, label: t(`workOrders.priority.${p}`) })),
          ]}
        />
        <ForgeSelect
          label={t('workOrders.column.assignee')}
          labelInline
          size="sm"
          value={filters.assigneeId}
          onValueChange={(value) => commit({ ...filters, assigneeId: value })}
          options={[
            { value: 'all', label: t('workOrders.filter.anyAssignee') },
            ...assignees.map((person) => ({ value: person.id, label: person.name })),
          ]}
        />
        <div role="group" aria-label={t('workOrders.column.source')} className="flex gap-1.5">
          {['all', 'ai', 'manual'].map((source) => (
            <Chip
              key={source}
              selected={filters.source === source}
              onClick={() => commit({ ...filters, source })}
            >
              {t(`workOrders.filter.source.${source}`)}
            </Chip>
          ))}
        </div>

        <label className="flex min-w-[12rem] flex-1 items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-2 focus-within:border-forge sm:max-w-[18rem]">
          <Icon name="search" size="sm" className="shrink-0 text-ink-mute" />
          <span className="sr-only">{t('workOrders.search')}</span>
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setPage(1)
            }}
            placeholder={t('workOrders.searchPlaceholder')}
            className="w-full border-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-dim"
          />
        </label>

        {activeFilters || query.trim() ? (
          <ForgeButton
            variant="ghost"
            size="sm"
            onClick={() => {
              commit(EMPTY)
              setQuery('')
            }}
          >
            {t('workOrders.clear')}
          </ForgeButton>
        ) : null}
      </div>

      {gatePending ? (
        <SkeletonRows count={5} />
      ) : !canRead ? (
        <EmptyState
          icon="lock"
          title={t('workOrders.forbidden')}
          body={t('workOrders.forbiddenBody')}
        />
      ) : board.isError ? (
        <ErrorState
          error={board.error}
          title={t('workOrders.loadFailed')}
          onRetry={() => board.refetch()}
        />
      ) : board.isPending ? (
        <SkeletonRows count={5} />
      ) : orders.length === 0 ? (
        <EmptyState
          icon="wrench"
          title={t('workOrders.emptyTitle')}
          body={t('workOrders.emptyBody')}
          action={
            canManage ? (
              <ForgeButton variant="primary" onClick={() => setCreating(true)}>
                <Icon name="plus" size="xs" />
                {t('workOrders.new')}
              </ForgeButton>
            ) : null
          }
        />
      ) : view === 'board' ? (
        <WorkOrderBoard
          orders={orders}
          columns={board.data?.columns ?? {}}
          advancing={advance.isPending ? advance.variables?.id : null}
          onOpen={(order) => setParam('wo', order.id)}
          onAdvance={(order) => move(order, nextStateOf(order))}
        />
      ) : (
        <WorkOrderTable
          orders={orders}
          total={board.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          onOpen={(order) => setParam('wo', order.id)}
        />
      )}

      <WorkOrderDetailDialog
        order={open ? (openDetail.data ?? open) : null}
        canManage={canManage}
        pending={advance.isPending || remove.isPending || !openDetail.data}
        onClose={() => setParam('wo', null)}
        onAdvance={async (order) => {
          await move(order, nextStateOf(order))
        }}
        onHold={async (order) => {
          await move(order, 'onHold')
        }}
        onCancel={async (order) => {
          await move(order, 'cancelled')
        }}
        onEdit={(order) => {
          setParam('wo', null)
          setEditing(order)
        }}
        onDelete={async (order) => {
          await remove.mutateAsync(order.id)
          setParam('wo', null)
          toast(t('workOrders.deleted', { id: order.serialNumber }), 'ok')
        }}
      />

      {/* Keyed so the draft reinitialises when the dialog is opened on a
          different order — see the note in the component. */}
      <WorkOrderFormDialog
        key={editing?.id ?? prefill?.assetId ?? (creating ? 'new' : 'closed')}
        open={creating || Boolean(prefill) || Boolean(editing)}
        order={editing}
        prefill={prefill}
        assignees={assignees}
        anomalies={anomalies}
        canPickAnomaly={canPickAnomaly}
        anomaliesFailed={anomalyList.isError}
        pending={create.isPending || save.isPending}
        onClose={closeForm}
        onSubmit={async (body) => {
          if (editing) {
            /* The status travels with the replace, unchanged. The form does not
               offer it — the workflow owns it — but `WorkOrderUpdate` requires
               it, and leaving it out would reset the order to whatever the
               server defaults to. */
            await save.mutateAsync({ id: editing.id, status: editing.status, ...body })
            toast(t('workOrders.saved', { id: editing.serialNumber }), 'ok')
          } else {
            /* `body.anomalyId` is the form's own, seeded from the prefill and
               changeable in the select. It used to be overridden by the prefill
               here, which made the control unusable the moment one existed. */
            const made = await create.mutateAsync(body)
            toast(t('workOrders.created', { id: made.serialNumber }), 'ok')
            setParam('wo', made.id)
          }
          closeForm()
        }}
      />
    </Page>
  )
}
